// ═══════════════════════════════════════════════════════════════════════════
// IMPROVED ADMIN DASHBOARD FONTS
// ═══════════════════════════════════════════════════════════════════════════
// Simple font improvements applied globally:
// - Medium font size throughout (minimum 12.5px)
// - Bold weight for small text (better readability)
// - No customization panel needed
// ═══════════════════════════════════════════════════════════════════════════

import { createTheme } from "@mui/material/styles";

// ── Font scale (Medium size - minimum 12.5px) ────────────────────────────────
export const FONT_SIZES = {
  // Base sizes (medium) - MINIMUM 12.5px
  xs: 12.5,      // Extra small (labels, captions) - BOLD
  sm: 13,        // Small (table text, secondary) - BOLD
  base: 14,      // Normal body text
  md: 15,        // Medium emphasis
  lg: 16,        // Headings
  xl: 18,        // Large headings
  xxl: 20,       // Page titles
};

// ── Font weights ─────────────────────────────────────────────────────────────
export const FONT_WEIGHTS = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

// ─────────────────────────────────────────────────────────────────────────────
// Enhanced Theme Typography
// ─────────────────────────────────────────────────────────────────────────────
// Apply this to your MUI theme to get improved fonts globally

export function getEnhancedTypography() {
  return {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    
    // Base font size
    fontSize: FONT_SIZES.base,
    
    // Heading variants
    h1: { fontSize: FONT_SIZES.xxl, fontWeight: FONT_WEIGHTS.bold },
    h2: { fontSize: FONT_SIZES.xl,  fontWeight: FONT_WEIGHTS.bold },
    h3: { fontSize: FONT_SIZES.lg,  fontWeight: FONT_WEIGHTS.bold },
    h4: { fontSize: FONT_SIZES.md,  fontWeight: FONT_WEIGHTS.semibold },
    h5: { fontSize: FONT_SIZES.base, fontWeight: FONT_WEIGHTS.semibold },
    h6: { fontSize: FONT_SIZES.sm,  fontWeight: FONT_WEIGHTS.semibold },
    
    // Body text
    body1: { fontSize: FONT_SIZES.base, fontWeight: FONT_WEIGHTS.normal },
    body2: { fontSize: FONT_SIZES.sm,   fontWeight: FONT_WEIGHTS.bold },  // Small text = BOLD
    
    // Button text
    button: { fontSize: FONT_SIZES.base, fontWeight: FONT_WEIGHTS.semibold, textTransform: "none" },
    
    // Caption (very small text) - BOLD for readability
    caption: { fontSize: FONT_SIZES.xs, fontWeight: FONT_WEIGHTS.bold },
    
    // Overline
    overline: { fontSize: FONT_SIZES.xs, fontWeight: FONT_WEIGHTS.bold, textTransform: "uppercase" },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Global CSS for Better Font Rendering
// ─────────────────────────────────────────────────────────────────────────────
// Add this to your global styles or index.css

export const GLOBAL_FONT_STYLES = `
  /* ── Better font rendering ────────────────────────────────────────────── */
  * {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }
  
  /* ── Base text styles ──────────────────────────────────────────────────── */
  body {
    font-size: ${FONT_SIZES.base}px;
    font-weight: ${FONT_WEIGHTS.normal};
    line-height: 1.5;
  }
  
  /* ── Small text (12.5-13px) should be BOLD ─────────────────────────────── */
  .MuiTableCell-root {
    font-size: ${FONT_SIZES.sm}px !important;
    font-weight: ${FONT_WEIGHTS.bold} !important;
  }
  
  .MuiChip-label {
    font-size: ${FONT_SIZES.xs}px !important;
    font-weight: ${FONT_WEIGHTS.bold} !important;
  }
  
  .MuiTypography-caption {
    font-size: ${FONT_SIZES.xs}px !important;
    font-weight: ${FONT_WEIGHTS.bold} !important;
  }
  
  .MuiMenuItem-root {
    font-size: ${FONT_SIZES.sm}px !important;
  }
  
  /* ── Input labels (small) = BOLD ───────────────────────────────────────── */
  .MuiInputLabel-root {
    font-size: ${FONT_SIZES.sm}px !important;
    font-weight: ${FONT_WEIGHTS.semibold} !important;
  }
  
  .MuiInputBase-input {
    font-size: ${FONT_SIZES.base}px !important;
  }
  
  /* ── Button text = medium weight ───────────────────────────────────────── */
  .MuiButton-root {
    font-size: ${FONT_SIZES.base}px !important;
    font-weight: ${FONT_WEIGHTS.semibold} !important;
  }
  
  /* ── Tab text ──────────────────────────────────────────────────────────── */
  .MuiTab-root {
    font-size: ${FONT_SIZES.base}px !important;
    font-weight: ${FONT_WEIGHTS.semibold} !important;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Direct MUI Theme Override
// ─────────────────────────────────────────────────────────────────────────────
// Use this to enhance your existing theme

export function enhanceThemeWithBetterFonts(existingTheme) {
  return createTheme({
    ...existingTheme,
    typography: {
      ...existingTheme.typography,
      ...getEnhancedTypography(),
    },
    components: {
      ...existingTheme.components,
      
      // Table cells - small text with BOLD
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: FONT_SIZES.sm,
            fontWeight: FONT_WEIGHTS.bold,
          },
        },
      },
      
      // Chips - very small text with BOLD
      MuiChip: {
        styleOverrides: {
          label: {
            fontSize: FONT_SIZES.xs,
            fontWeight: FONT_WEIGHTS.bold,
          },
        },
      },
      
      // Typography caption - BOLD
      MuiTypography: {
        styleOverrides: {
          caption: {
            fontSize: FONT_SIZES.xs,
            fontWeight: FONT_WEIGHTS.bold,
          },
          body2: {
            fontSize: FONT_SIZES.sm,
            fontWeight: FONT_WEIGHTS.bold,
          },
        },
      },
      
      // Buttons
      MuiButton: {
        styleOverrides: {
          root: {
            fontSize: FONT_SIZES.base,
            fontWeight: FONT_WEIGHTS.semibold,
            textTransform: "none",
          },
        },
      },
      
      // Input labels
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: FONT_SIZES.sm,
            fontWeight: FONT_WEIGHTS.semibold,
          },
        },
      },
    },
  });
}

export default {
  FONT_SIZES,
  FONT_WEIGHTS,
  getEnhancedTypography,
  enhanceThemeWithBetterFonts,
  GLOBAL_FONT_STYLES,
};